import type { ReferenceImage } from '@/components/MessageList/ReferenceUIMessage/const';
import type { AutoInputFile } from '@/stores/autoInput';
import { normalizeReferenceUiImageUrl } from '@/components/Markdown/MarkdownViewer/CustomTagCom/GenStartButton/helper';
import { useProjectStore } from '@/stores/project';
import { useReferenceUiStore } from '@/stores/referenceUi';
import { buildContinueGenerationInputContent } from './continueGeneration';

interface GenerationReferenceImageSource {
  referenceImageUrl?: string;
  projectName?: string;
  positioning?: string;
  visualDirection?: string;
}

interface ContinueGenerationAutoInput {
  content: string;
  files: AutoInputFile[];
  displayContent: string;
}

function buildReferenceImageFile(url: string, name?: string): AutoInputFile {
  return {
    url: normalizeReferenceUiImageUrl(url),
    name: name ? `${name}-visual-reference.png` : 'reference-visual-reference.png',
    mimeType: 'image/png',
    preserveMultimodal: true,
  };
}

function getSelectedReferenceImageFile(projectId?: string): AutoInputFile | null {
  if (!projectId) {
    return null;
  }

  const session = useReferenceUiStore.getState().sessionsByProjectId[projectId];
  const selectedImage = session?.selectedImage;
  if (!selectedImage?.url) {
    return null;
  }

  return buildReferenceImageFile(
    selectedImage.url,
    selectedImage.styleDisplayName || selectedImage.styleName || 'reference',
  );
}

export function buildGenerationReference(source?: GenerationReferenceImageSource | null): ReferenceImage | undefined {
  if (!source?.referenceImageUrl) {
    return undefined;
  }

  const styleName = source.visualDirection || source.projectName || 'reference';
  return {
    url: normalizeReferenceUiImageUrl(source.referenceImageUrl),
    styleName,
    styleDisplayName: styleName,
    styleDescription: source.positioning,
  };
}

export function buildGenerationReferenceImageFiles(
  source?: GenerationReferenceImageSource | null,
  projectId = useProjectStore.getState().projectId,
): AutoInputFile[] {
  if (source?.referenceImageUrl) {
    return [buildReferenceImageFile(source.referenceImageUrl, source.projectName || 'reference')];
  }

  const selectedReferenceImageFile = getSelectedReferenceImageFile(projectId);
  return selectedReferenceImageFile ? [selectedReferenceImageFile] : [];
}

export function buildContinueGenerationAutoInput(
  continueLabel: string,
  source?: GenerationReferenceImageSource | null,
  projectId?: string,
): ContinueGenerationAutoInput {
  const files = buildGenerationReferenceImageFiles(source, projectId);

  return {
    content: buildContinueGenerationInputContent(continueLabel, {
      hasReferenceImage: files.length > 0,
      referenceUiConfirmed: files.length > 0,
    }),
    files,
    displayContent: continueLabel,
  };
}
